import { AlertTriangle, ArrowDownToLine, CheckCircle2, FileSpreadsheet, FileText, ShieldAlert, Sparkles, TrendingUp, Users } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, currency } from "@/lib/api";

const reportEndpoint = (report) => {
  if (report.startsWith("Referrals")) return "/admin/export/referrals.xlsx";
  if (report.startsWith("Payout")) return "/admin/export/payout-register.pdf";
  if (report.startsWith("TDS")) return "/admin/export/tds-register.xlsx";
  return null;
};

const downloadReport = async (report) => {
  const endpoint = reportEndpoint(report);
  if (!endpoint) {
    toast.info(`${report} is available after campaign data is connected`);
    return;
  }
  try {
    const response = await api.get(endpoint, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = endpoint.split("/").pop();
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success(`${report} downloaded`);
  } catch (error) {
    toast.error(error.response?.data?.detail || "Export failed");
  }
};

const Kpi = ({ label, value, icon: Icon, feature }) => (
  <div className={`premium-card admin-kpi ${feature ? "kpi-feature" : ""}`}>
    <div className="kpi-icon"><Icon size={18} /></div><p>{label}</p>
    <strong className="tabular">{value}</strong>
    <span><TrendingUp size={13} /> Live system total</span>
  </div>
);

const KpiGrid = ({ kpi }) => (
  <div className="admin-kpi-grid">
    <Kpi label="Total leads" value={kpi.total_leads || 0} icon={Users} />
    <Kpi label="Conversion rate" value={`${kpi.conversion_rate || 0}%`} icon={TrendingUp} />
    <Kpi label="Referral revenue" value={currency(kpi.referral_revenue)} icon={FileSpreadsheet} feature />
    <Kpi label="Rewards paid" value={currency(kpi.rewards_paid)} icon={CheckCircle2} />
  </div>
);

const ConversionFunnel = ({ pipeline, totalLeads }) => (
  <section className="premium-card feature-card">
    <div className="section-head compact"><div><p className="eyebrow">ALL CHANNELS · YTD</p><h2>Conversion funnel</h2></div></div>
    <div className="admin-funnel">
      {Object.entries(pipeline || {}).map(([stage, count]) => (
        <div key={stage}><span>{stage.replace("_", " ")}</span><strong>{count}</strong><div><i style={{ width: `${Math.min(100, (count / Math.max(1, totalLeads)) * 100)}%` }} /></div></div>
      ))}
    </div>
  </section>
);

const ActionRow = ({ className, icon: Icon, title, subtitle, count }) => (
  <div className={`action-row ${className}`}><Icon /><div><b>{title}</b><span>{subtitle}</span></div><strong>{count}</strong></div>
);

const ActionQueue = ({ pendingCount, fraudCount }) => (
  <section className="premium-card action-card" data-testid="admin-action-queue">
    <div className="section-head compact"><div><p className="eyebrow">OPERATIONS</p><h2>Action required</h2></div><span className="action-count">{pendingCount + fraudCount}</span></div>
    <div className="action-list">
      <ActionRow className="danger" icon={ShieldAlert} title="High-value approvals" subtitle="No high-value items detected" count={0} />
      <ActionRow className="warn" icon={Sparkles} title="Standard approvals pending" subtitle="Calculated and awaiting review" count={pendingCount} />
      <ActionRow className="warn" icon={AlertTriangle} title="Fraud signals" subtitle="Rules-based review queue" count={fraudCount} />
      <ActionRow className="ok" icon={CheckCircle2} title="Partner onboarding" subtitle="Profiles requiring ERP supplier link" count={0} />
    </div>
  </section>
);

const SegmentPerformance = ({ segments }) => (
  <section className="premium-card feature-card">
    <div className="section-head compact"><div><p className="eyebrow">PROGRAM MIX</p><h3>Segment performance</h3></div></div>
    {Object.entries(segments || {}).map(([segment, count]) => <div className="segment-row" key={segment}><span>{segment.replace("_", " ")}</span><div><i style={{ width: `${Math.min(100, count * 8)}%` }} /></div><b>{count}</b></div>)}
  </section>
);

const Leaderboard = ({ rows }) => (
  <section className="premium-card feature-card">
    <div className="section-head compact"><div><p className="eyebrow">CROSS-SEGMENT</p><h3>Top performers</h3></div></div>
    {rows?.length ? rows.map((row, index) => <div className="performer-row" key={`${row.name}-${index}`}><span>{index + 1}</span><div><b>{row.name}</b><small>{row.role.replace("_", " ")}</small></div><strong>{row.referrals}</strong></div>) : <p className="empty-copy">No performance records yet.</p>}
  </section>
);

const ReportsPanel = ({ reports }) => (
  <section className="premium-card feature-card reports-card">
    <div className="section-head compact"><div><p className="eyebrow">COMPLIANCE</p><h3>Reports & export</h3></div></div>
    {(reports || []).map((report, index) => <button key={report} data-testid={`report-${index}`} onClick={() => downloadReport(report)}><span>{report.includes("PDF") ? <FileText /> : <FileSpreadsheet />}{report}</span><ArrowDownToLine /></button>)}
  </section>
);

export const AdminOverview = ({ overview, pendingRewards = [], fraud = [] }) => {
  const kpi = overview?.kpis || {};
  return (
    <div className="space-y-6">
      <KpiGrid kpi={kpi} />
      <div className="admin-main-grid"><ConversionFunnel pipeline={overview?.pipeline} totalLeads={kpi.total_leads || 0} /><ActionQueue pendingCount={pendingRewards.length} fraudCount={fraud.length} /></div>
      <div className="admin-bottom-grid"><SegmentPerformance segments={overview?.segments} /><Leaderboard rows={overview?.leaderboard} /><ReportsPanel reports={overview?.reports} /></div>
    </div>
  );
};
